import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CellComponent, RowComponent, Tabulator } from 'tabulator-tables';
import useInfraFacilityManagement from '@/domains/asset-management/hooks/useInfraFacilityManagement';
import type {
  ButtonState,
  CreateInfraFacilityPayload,
  InfraFacilityDetailData,
  InfraFacilityFormValues,
  InfraFacilityType,
  UpdateInfraFacilityPayload,
} from '@/domains/asset-management/types/infra-facility';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import { ReactTabulator } from '@/shared/components/ui/LocalizedTabulator/LocalizedTabulator';
import LogList from '@/shared/components/ui/LogList/LogList';
import Select from '@/shared/components/ui/Select/Select';
import TablePagination from '@/shared/components/ui/TablePagination/TablePagination';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import { remoteSortHandler, useServerTable } from '@/shared/hooks/useServerTable';
import { formatFullDateTimeDDMMYYYY } from '@/shared/utils/date';

const commonColumnProps = {
  hozAlign: 'center' as const,
  headerHozAlign: 'center' as const,
  resizable: false as const,
};

const SORT_FIELD_MAP: Record<string, string> = {
  infra_and_facility_id: 'id',
  updated_time: 'updated_time',
};

const emptyFormValues: InfraFacilityFormValues = {
  infra_and_facility_id: null,
  site_id: null,
  infra_and_facility_detail_type: null,
  name: null,
  latitude: null,
  longitude: null,
};

export interface InfraFacilityManagementViewProps {
  /** Fixed scope of this screen - INFRA_TP_INFRA (Infrastructure) or INFRA_TP_FACILITY (Facility). */
  infraAndFacilityType: InfraFacilityType;
  breadcrumbLabel: string;
  typeColumnTitle: string;
  nameColumnTitle: string;
  typeFieldLabel: string;
  nameFieldLabel: string;
  searchPlaceholder: string;
}

/** Shared UI for InfraManagement.tsx and FacilityManagement.tsx - both are
 * filtered views of the same infra_and_facility table/endpoint, distinguished
 * only by infra_and_facility_type (INFRA_TP_INFRA | INFRA_TP_FACILITY). */
const InfraFacilityManagementView = ({
  infraAndFacilityType,
  breadcrumbLabel,
  typeColumnTitle,
  nameColumnTitle,
  typeFieldLabel,
  nameFieldLabel,
  searchPlaceholder,
}: InfraFacilityManagementViewProps) => {
  const { t } = useTranslation();

  const tbRef = useRef<Tabulator | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const [disabledForm, setDisabledForm] = useState<boolean>(true);
  const [hasChangesUpdate, setHasChangesUpdate] = useState<boolean>(false);
  const [hasChangesCreate, setHasChangesCreate] = useState<boolean>(false);
  const [isNewClicked, setIsNewClicked] = useState<boolean>(false);
  const hasChangesUpdateRef = useRef(hasChangesUpdate);
  const hasChangesCreateRef = useRef(hasChangesCreate);
  const [newId, setNewId] = useState<number | null>(null);
  const [selectedItem, setSelectedItem] = useState<{ infra_and_facility_id: number | null }>({
    infra_and_facility_id: null,
  });
  const selectedItemRef = useRef(selectedItem);
  const [detailTypeFilter, setDetailTypeFilter] = useState<string>('');

  useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
  }, []);

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
    selectedItemRef.current = selectedItem;
  }, [hasChangesUpdate, hasChangesCreate, selectedItem]);

  const table = useServerTable<{
    infra_and_facility_type: string;
    input?: string;
    infra_and_facility_detail_type?: string;
  }>({
    initialFilters: { infra_and_facility_type: infraAndFacilityType },
    defaultSort: { field: 'updated_time', dir: 'desc' },
    defaultSize: 10,
  });
  const queryParams = table.queryString;

  const { commonListData } = useCommonCodes({ optionParams: `upper-code=${infraAndFacilityType}` });
  const detailTypeOptions = useMemo(
    () =>
      commonListData?.[infraAndFacilityType]?.code?.map((code, index) => ({
        value: code ?? '',
        label: commonListData[infraAndFacilityType]?.name[index] ?? '',
      })) ?? [],
    [commonListData, infraAndFacilityType]
  );

  const [formValues, setFormValues] = useState<InfraFacilityFormValues>(emptyFormValues);
  const emptyDetail = useCallback(() => setFormValues(emptyFormValues), []);

  const [buttonState, setButtonState] = useState<ButtonState>({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  const disableAllButtons = useCallback(() => {
    setButtonState({ confirm: true, cancel: true, delete: true, create: true });
  }, []);

  const enableInitialButtons = useCallback(() => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prev) => ({ ...prev, create: false }));
  }, [disableAllButtons]);

  const enableUPDATEButtons = useCallback(() => {
    disableAllButtons();
    setButtonState((prev) => ({ ...prev, cancel: false, delete: false, create: false }));
  }, [disableAllButtons]);

  const enableRegisterButtons = useCallback(() => {
    disableAllButtons();
    setButtonState((prev) => ({ ...prev, cancel: false }));
  }, [disableAllButtons]);

  const reloadCallback = useCallback(() => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    emptyDetail();
    setDisabledForm(true);
    setHasChangesUpdate(false);
    setIsNewClicked(false);
    setHasChangesCreate(false);
    setSelectedItem({ infra_and_facility_id: null });
    setNewId(null);
  }, [enableInitialButtons, emptyDetail]);

  const {
    infraFacilityListData,
    detailInfraFacilityData,
    deleteInfraFacility,
    createInfraFacility,
    updateInfraFacility,
    refetchDetailInfraFacility,
  } = useInfraFacilityManagement({
    infraFacilityId: selectedItem?.infra_and_facility_id,
    queryParams,
    onDeleteSuccess: reloadCallback,
    onUpdateSuccess: () => {
      refetchDetailInfraFacility();
      enableUPDATEButtons();
      setIsNewClicked(false);
      setHasChangesUpdate(false);
    },
    onCreateSuccess: (responseData) => {
      reloadCallback();
      setNewId(responseData.infra_and_facility_id);
    },
  });

  const pageData = infraFacilityListData?.data;
  const rows = pageData?.rows ?? [];
  const dataItem = detailInfraFacilityData?.data;

  const columns = useMemo(
    () => [
      {
        title: t('cmn > no'),
        formatter: (cell: CellComponent) =>
          (table.page - 1) * table.size + cell.getRow().getPosition(true),
        width: 60,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t(typeColumnTitle),
        field: 'infra_and_facility_detail_type_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t(nameColumnTitle),
        field: 'name',
        widthGrow: 1,
        headerSort: true,
        ...commonColumnProps,
      },
      {
        title: t('cmn > latitude'),
        field: 'latitude',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('cmn > longitude'),
        field: 'longitude',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
    ],
    [table.page, table.size, typeColumnTitle, nameColumnTitle, t]
  );

  const detailToFormValues = useCallback(
    (data: InfraFacilityDetailData): InfraFacilityFormValues => ({
      infra_and_facility_id: data.infra_and_facility_id,
      site_id: data.site_id,
      infra_and_facility_detail_type: data.infra_and_facility_detail_type,
      name: data.name,
      latitude: data.latitude,
      longitude: data.longitude,
    }),
    []
  );

  useEffect(() => {
    if (dataItem) setFormValues(detailToFormValues(dataItem));
  }, [dataItem, detailToFormValues]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormValues((prev) => ({ ...prev, [name]: value }));

      if (isNewClicked) {
        setHasChangesCreate(true);
      } else if (selectedItemRef.current?.infra_and_facility_id) {
        setHasChangesUpdate(true);
      }
    },
    [isNewClicked]
  );

  const languageTabulator = () => ({
    pagination: {
      first: t('cmn > first page'),
      first_title: t('cmn > first page'),
      last: t('cmn > last page'),
      last_title: t('cmn > last page'),
      prev: t('cmn > page before'),
      prev_title: t('cmn > page before'),
      next: t('cmn > next page'),
      next_title: t('cmn > next page'),
    },
  });

  const optionsTabulator = {
    debugInvalidOptions: true,
    // Server-side pagination: Tabulator's own pager is off; <TablePagination> drives it.
    pagination: false,
    rowHeight: 41,
    selectableRows: false,
    movableRows: false,
    resizableRows: false,
    index: 'infra_and_facility_id',
    locale: 'ko',
    langs: { ko: languageTabulator() },
    selectableRowsCheck: (row: RowComponent) =>
      !row.getElement().classList.contains('tabulator-selected'),
    placeholder: `<div style='text-align:center;'>${t('msg > no data')}</div>`,
  };

  const handleSearch = useCallback(
    (inputVal: string) => {
      table.setFilters({ input: inputVal || undefined });
    },
    [table]
  );

  const handleReset = useCallback(() => {
    setDetailTypeFilter('');
    table.reset();
  }, [table]);

  const handleDetailTypeFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setDetailTypeFilter(e.target.value);
      table.setFilters({ infra_and_facility_detail_type: e.target.value || undefined });
    },
    [table]
  );

  const handleRowSelected = useCallback(
    (row: RowComponent) => {
      const selectRow = () => {
        tbRef.current?.deselectRow();
        row.select();
        row.getElement().classList.add('custom-hover');

        const rowData = row.getData();
        setSelectedItem({ infra_and_facility_id: rowData.infra_and_facility_id });
        setDisabledForm(false);
        setHasChangesUpdate(false);
        setHasChangesCreate(false);
        setIsNewClicked(false);
        enableUPDATEButtons();

        requestAnimationFrame(() => nameRef.current?.focus());
      };

      if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
        message.confirmClicked().then(selectRow);
      } else {
        selectRow();
      }
    },
    [enableUPDATEButtons, t]
  );

  const handleNewButtonClick = useCallback(() => {
    const startNew = () => {
      tbRef.current?.deselectRow();
      emptyDetail();
      setIsNewClicked(true);
      enableRegisterButtons();
      setSelectedItem({ infra_and_facility_id: null });
      setDisabledForm(false);
      setHasChangesUpdate(false);
      requestAnimationFrame(() => nameRef.current?.focus());
    };

    if (hasChangesCreate || hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
      message.confirmClicked().then(startNew);
    } else {
      startNew();
    }
  }, [hasChangesCreate, hasChangesUpdate, enableRegisterButtons, t, emptyDetail]);

  const handleCancelButtonClick = useCallback(() => {
    if (hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        setHasChangesUpdate(false);
        setSelectedItem({ infra_and_facility_id: null });
        enableInitialButtons();
      });
    } else if (isNewClicked) {
      if (hasChangesCreate) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
        message.confirmClicked().then(() => {
          emptyDetail();
          setIsNewClicked(false);
          setDisabledForm(true);
          enableInitialButtons();
          setHasChangesCreate(false);
        });
      } else {
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        enableInitialButtons();
        setHasChangesCreate(false);
      }
    } else {
      reloadCallback();
    }
  }, [
    hasChangesUpdate,
    hasChangesCreate,
    isNewClicked,
    enableInitialButtons,
    reloadCallback,
    t,
    emptyDetail,
  ]);

  const buildPayload = useCallback((): CreateInfraFacilityPayload | null => {
    const { name, infra_and_facility_detail_type } = formValues;
    if (!name?.trim() || !infra_and_facility_detail_type) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > required values'));
      return null;
    }
    return {
      infra_and_facility_type: infraAndFacilityType,
      infra_and_facility_detail_type,
      name: name.trim(),
      latitude:
        formValues.latitude === '' || formValues.latitude === null
          ? null
          : Number(formValues.latitude),
      longitude:
        formValues.longitude === '' || formValues.longitude === null
          ? null
          : Number(formValues.longitude),
    };
  }, [formValues, infraAndFacilityType, t]);

  const handleRegistButtonClick = useCallback(() => {
    const payload = buildPayload();
    if (!payload) return;
    createInfraFacility(payload);
  }, [createInfraFacility, buildPayload]);

  const handleConfirmButtonClick = useCallback(() => {
    const payload = buildPayload();
    if (!payload || !formValues.infra_and_facility_id) return;
    const updatePayload: UpdateInfraFacilityPayload = {
      ...payload,
      infra_and_facility_id: Number(formValues.infra_and_facility_id),
    };
    updateInfraFacility({
      infraFacilityData: updatePayload,
      params: { infra_and_facility_id: updatePayload.infra_and_facility_id },
    });
  }, [formValues, updateInfraFacility, buildPayload]);

  const handleDeleteButtonClick = useCallback(() => {
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('msg > delete confirm'), { mode: 'confirm' });
    message.confirmClicked().then(() => {
      if (selectedItem?.infra_and_facility_id)
        deleteInfraFacility(selectedItem.infra_and_facility_id);
    });
  }, [selectedItem, deleteInfraFacility, t]);

  const logData = dataItem
    ? [
        { label: t('cmn > registered by'), value: dataItem.registered_by },
        {
          label: t('cmn > registered time'),
          value: dataItem.registered_time
            ? formatFullDateTimeDDMMYYYY(new Date(dataItem.registered_time))
            : dataItem.registered_time,
        },
        { label: t('cmn > updated by'), value: dataItem.updated_by },
        {
          label: t('cmn > updated time'),
          value: dataItem.updated_time
            ? formatFullDateTimeDDMMYYYY(new Date(dataItem.updated_time))
            : dataItem.updated_time,
        },
      ]
    : [];

  return (
    <section className="wrap">
      <div className="header-title">
        <h3>{t('ASSET')}</h3>
        <h3>&gt;</h3>
        <h3>{t(breadcrumbLabel)}</h3>
      </div>

      <ContainerCard>
        <Filtering
          searchRef={searchRef}
          labelSelect={t('cmn > filter by', { label: t(typeFieldLabel) })}
          placeholder={t(searchPlaceholder)}
          disableFiltering={true}
          onReset={handleReset}
          onSearch={handleSearch}
        >
          <Select
            value={detailTypeFilter}
            onChange={handleDetailTypeFilterChange}
            options={[{ value: '', label: t('cmn > all') }, ...detailTypeOptions]}
          />
        </Filtering>
      </ContainerCard>

      <ContainerCard>
        <div className="table-container relative">
          <ReactTabulator
            data={rows}
            columns={columns}
            layout="fitDataStretch"
            options={optionsTabulator}
            className="tabulator-custom"
            onRef={(r) => {
              tbRef.current = r.current;
            }}
            events={{
              rowClick: (_e: UIEvent, row: RowComponent) => handleRowSelected(row),
              dataSorted: remoteSortHandler(table, SORT_FIELD_MAP),
              tableBuilt: () => {
                if (!tbRef.current || !newId) return;
                try {
                  const row = tbRef.current.getRow(newId) || null;
                  if (row && typeof row.select === 'function') {
                    tbRef.current.scrollToRow(row, 'bottom', true);
                    handleRowSelected(row);
                  }
                } catch {
                  /* row not found */
                }
              },
            }}
          />
        </div>
        <TablePagination
          total={pageData?.total ?? 0}
          page={table.page}
          size={table.size}
          totalPages={pageData?.total_pages ?? 0}
          onPageChange={table.setPage}
          centered
        />
      </ContainerCard>

      <ContainerCard className="flex flex-col">
        <div className="grid grid-cols-1 items-center gap-6">
          <div className="grid grid-cols-1 gap-3">
            <DetailForm
              className="items-center!"
              label={t(typeFieldLabel)}
              required={true}
              disabled={disabledForm}
              inputType="select"
              onChange={handleInputChange}
              value={formValues.infra_and_facility_detail_type || ''}
              name="infra_and_facility_detail_type"
              optionSelect={detailTypeOptions}
            />
            <DetailForm
              className="items-center!"
              label={t(nameFieldLabel)}
              formRef={nameRef}
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.name || ''}
              name="name"
              maxLength={100}
            />
            <DetailForm
              className="items-center!"
              label={t('cmn > latitude longitude')}
              inputType="text"
              showInput={false}
            >
              <div className="flex w-full flex-row gap-x-2">
                <GeneralInput
                  customInput="w-full"
                  disabled={disabledForm}
                  onChange={handleInputChange}
                  value={formValues.latitude ?? ''}
                  name="latitude"
                  maxLength={100}
                  pattern={'^-?[0-9.]*$'}
                />
                <GeneralInput
                  customInput="w-full"
                  disabled={disabledForm}
                  onChange={handleInputChange}
                  value={formValues.longitude ?? ''}
                  name="longitude"
                  maxLength={100}
                  pattern={'^-?[0-9.]*$'}
                />
              </div>
            </DetailForm>
          </div>
        </div>
        <hr className="border-t border-gray-300" />
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex-1">{disabledForm ? null : <LogList logs={logData} />}</div>
          <div className="flex-none">
            <ButtonGroup
              onClickDelete={handleDeleteButtonClick}
              onClickNew={handleNewButtonClick}
              onClickCancel={handleCancelButtonClick}
              onClickRegist={handleRegistButtonClick}
              onClickConfirm={handleConfirmButtonClick}
              isNewClicked={isNewClicked}
              cancelButtonState={buttonState.cancel}
              confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
              deleteButtonState={buttonState.delete}
              newButtonState={hasChangesCreate ? false : buttonState.create}
            />
          </div>
        </div>
      </ContainerCard>
    </section>
  );
};

export default InfraFacilityManagementView;
